import jilProducts from 'api/jil/jilProducts';
import OrganizationUser from 'models/organizationUser/OrganizationUser';
import eventBus from 'services/events/eventBus';
import feature from 'services/feature';
import MemberList from 'services/member/MemberList';

import {PRODUCT_ADMIN_LIST_CACHE_ID, PRODUCT_ADMIN_LIST_EVENT} from './ProductAdminListConstants';

/**
 * @description Method to instantiate a ProductAdminList.
 * @param {Object} options - Options for the constructor.
 * @param {String} options.productId The product id.
 * @param {Array} [options.roles] roles. If product has 'isProductSupportRoleAssignmentAllowedArray'
 *  FI this is an array of roles for admin. Options are PRODUCT_ADMIN and PRODUCT_SUPPORT_ADMIN.
 *  If product doesn't have the FI it should be undefined.
 * @returns {ProductAdminList} orgList - new LicenseGroupAdminList.
 */
class ProductAdminList extends MemberList {
  /**
   * @description Constructor for LicenseGroupAdminList model Objects.
   */
  constructor({isCacheable, productId, roles, ...options}) {
    super({
      isCacheable: feature.isEnabled('temp_src2_cache'), // remove entirely with temp_src2_cache
      itemClassRef: OrganizationUser,
      modelCacheId: PRODUCT_ADMIN_LIST_CACHE_ID,
      refreshResource: jilProducts.getProductAdmins,
      saveResource: jilProducts.patchProductAdmins,
      ...options,
    });

    this.roles = roles;
    this.productId = productId;
  }

  /**
   * @description overidden key method to be used when we fetch models from modelCache.
   * @deprecated Remove with temp_src2_cache
   *
   * @returns {String} key formed from local params and JilModelList params.
   */
  getKey() {
    if (feature.isEnabled('temp_src2_cache')) {
      return super.getKey();
    }
    return super.getKey(getParams(this));
  }

  /**
   * @description Get params for api call to fetch list.
   *
   * @returns {Object} params to be passed to query
   */
  getQueryParams() {
    return {
      ...super.getQueryParams(),
      productId: this.productId,
      roles: this.roles?.join(','),
    };
  }

  /**
   * @description Method to execute after a successful refresh.
   */
  onRefreshSuccess() {
    eventBus.emit(PRODUCT_ADMIN_LIST_EVENT.UPDATE.LIST);
    if (this.shouldUpdateTotalItemCount()) {
      eventBus.emit(PRODUCT_ADMIN_LIST_EVENT.UPDATE.COUNT, this.pagination.itemCount);
    }
  }

  /**
   * @description Method to refresh the contents of the list.
   * @deprecated Remove with temp_src2_cache
   *
   * @returns {Promise} promise - resolved when the list is refreshed.
   */
  async refresh() {
    if (feature.isEnabled('temp_src2_cache')) {
      return super.refresh();
    }

    await super.refresh(getParams(this));
    eventBus.emit(PRODUCT_ADMIN_LIST_EVENT.UPDATE.LIST);
    if (this.shouldUpdateTotalItemCount()) {
      eventBus.emit(PRODUCT_ADMIN_LIST_EVENT.UPDATE.COUNT, this.pagination.itemCount);
    }
    return this;
  }

  /**
   * @description Method to save changes to admin list to back-end.
   *   In Admin Console, admins are added and edited using OrganizationUser.save().
   * @param {Object} [options] - options for saving to pass thru to MemberList.save()
   * @param {Boolean} [options.refresh] - whether to refresh after saving, defaults to true.
   * @returns {Promise} resolves if changes successfully saved, else rejects with error message.
   */
  save(options) {
    return super.save(getParams(this), options);
  }
}

///////////

/**
 * @description Fetch params used for this call.
 *              Rename to getSaveParams when temp_src2_cache is removed.
 *
 * @param {Object} model the model to pick params from.
 * @returns {Array} params to be passed to JIL or used as a key.
 */
function getParams(model) {
  return {
    ...(feature.isEnabled('temp_src2_cache') ? {} : {orgId: model.orgId}),
    productId: model.productId,
    roles: model.roles?.join(','),
  };
}

export default ProductAdminList;
